import React, { useState } from "react";
import { motion } from "framer-motion";
import { ContentItem } from "@/stores/tripBuilder/useToraStore";
import LazyLoad from "../../image/LazyLoad";
import { formatImageUrl, getLocationTypeIcon } from "@/lib/general";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tooltip } from "@heroui/tooltip";
import placeholderImage from "@/assets/imgloading.png";

interface PlaceCardProps {
  type: "place_details" | "viator_full";
  place: ContentItem;
  isSelected: boolean;
  onSelect: () => void;
}

const PlaceCard: React.FC<PlaceCardProps> = ({
  type,
  place,
  isSelected,
  onSelect,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="rounded-2xl border-neutral-200 border-1 my-3 transition-all duration-300 ease-in-out">
      <div className="p-3 sm:p-3">
        <div className="flex flex-col sm:flex-row items-start gap-4 h-[9rem]">
          {/* Image Section */}
          <div className="w-full sm:w-[10rem] h-[9rem] relative">
            {place.image?.url ? (
              <>
                <LazyLoad
                  src={formatImageUrl(place.image.url, "medium")}
                  lowResSrc={formatImageUrl(place.image.url, "low")}
                  alt={place.name}
                  className="h-full w-full rounded-lg sm:rounded-l-xl sm:rounded-r-md"
                  objectFit="cover"
                />
                <Button
                  size="icon"
                  variant="ghost"
                  className="absolute top-2 left-2 text-white hover:text-white bg-transparent hover:bg-white/15 backdrop-blur h-5 w-5 2xl:h-6 2xl:w-6 duration-300 ease-in-out rounded-full"
                  onClick={(e) => {
                    e.stopPropagation();
                    onSelect();
                  }}
                >
                  <i className="fal fa-circle-info text-xs 2xl:text-sm" />
                </Button>
              </>
            ) : (
              <div className="h-full w-full bg-neutral-200 flex items-center justify-center">
                <i className="fas fa-image text-neutral-400 text-3xl" />
                <Button
                  size="icon"
                  variant="ghost"
                  className="absolute top-2 left-2 text-neutral-600 hover:bg-slate-100 hover:text-slate-700 h-6 w-6 duration-300 ease-in-out rounded-full"
                  onClick={(e) => {
                    e.stopPropagation();
                    onSelect();
                  }}
                >
                  <i className="fal fa-circle-info text-[10px]" />
                </Button>
              </div>
            )}
          </div>

          {/* Content Section */}
          <div className="flex-1 min-w-0 flex flex-col h-full justify-between w-full">
            <div className="w-full">
              <div className="flex items-center flex-wrap gap-2">
                <h2 className="text-base font-medium text-neutral-800 truncate max-w-[calc(100%-4rem)]">
                  {place.name || ""}
                </h2>
              </div>

              {place.description && (
                <p className="mt-2 text-xs font-light text-neutral-700 line-clamp-3">
                  {place.description}
                </p>
              )}
            </div>

            {/* Actions Section */}
            <div className="flex justify-between items-center w-full">
              {/* Left group of utility buttons */}
              <div className="flex flex-row items-center space-x-3 mt-1">
                {place.types?.slice(0, 2).map((type) => (
                  <div
                    key={type}
                    className="flex flex-row items-center space-x-2 bg-neutral-100 rounded-full px-3 py-2"
                  >
                    <i
                      className={`far ${getLocationTypeIcon(
                        type
                      )} text-neutral-700 text-[10px]`}
                    ></i>
                    <p className="text-neutral-700 font-light text-[10px] leading-tight">
                      {type}
                    </p>
                  </div>
                ))}
              </div>
              {/* <div className="flex items-center gap-2">
                <button
                  onClick={onSelect}
                  className={`${
                    isSelected
                      ? "bg-green-50 text-green-600 border border-green-500"
                      : "bg-indigo-50 hover:bg-indigo-100 text-indigo-500 border border-indigo-500"
                  } w-24 h-8 2xl:w-28 2xl:h-9 rounded-full text-xs 2xl:text-sm font-light flex items-center justify-center gap-2 transition-all duration-300 ease-in-out`}
                >
                  <span className="transition-opacity duration-300 ease-in-out">
                    {isSelected ? "Selected" : "Select"}
                  </span>
                </button>
              </div> */}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlaceCard;
